import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, Scale } from 'lucide-react';
import { complianceApi } from '../services/complianceApi';
import { CHECK_STATUS, ComplianceOverview, leftLabel } from '../lib/compliance';
import { shortWon } from '../types/vehicle';

const won0 = (v: number) => (v === 0 ? '0원' : shortWon(v));

/**
 * 의무 일정 · 투자 의무 (2026-10-26) — 규칙은 서버(`modules/compliance/rules.ts`)가 계산한다.
 *   · vehicleId 가 있으면 그 자금원 것만(자금원 상세), 없으면 전체(대시보드)
 */
export const CompliancePanel: React.FC<{ vehicleId?: number; days?: number; version?: string }> = ({ vehicleId, days = 60, version }) => {
  const [data, setData] = useState<ComplianceOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // version — 자금원 규약 값이 바뀌면 다시 읽는다
  useEffect(() => { complianceApi.overview().then(setData).catch((e) => setErr((e as Error).message)); }, [version]);

  if (err) return <p className="text-xs text-red-600 mb-4">의무 일정을 불러오지 못했습니다: {err}</p>;
  if (!data) return null;
  const mine = <T extends { vehicleId: number | null }>(x: T) => vehicleId === undefined || x.vehicleId === vehicleId;
  const due = data.due.filter(mine).filter((x) => x.left <= days);
  // 기간 안에 없으면 그 다음 기한을 한 줄 — 빈 칸만 보이면 일정이 없는 것처럼 읽힌다
  const next = due.length === 0 ? data.due.filter(mine).filter((x) => x.left > days).sort((a, b) => a.left - b.left)[0] : undefined;
  const checks = data.checks.filter(mine);
  if (due.length === 0 && checks.length === 0) return null;

  return (
    <div data-tour="compliance" className="grid gap-4 lg:grid-cols-2 mb-5">
      <section className="bg-white border border-slate-200 rounded-lg">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">
          <CalendarClock size={14} className="text-slate-400" /> 다가오는 의무 <span className="font-normal text-slate-400">{days}일 안</span>
        </h2>
        {due.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">
            <p>{days}일 안에 기한 없음</p>
            {next && (
              <p className="mt-1 text-xs text-slate-400">
                다음 기한: <Link to={next.link} className="text-slate-600 hover:text-indigo-600">{vehicleId === undefined && <>{next.vehicleName} · </>}{next.title}</Link>
                {' '}{next.dueOn.replace(/-/g, '.')} ({leftLabel(next.left)})
              </p>
            )}
          </div>
        ) : (
          <ul>
            {due.map((x) => (
              <li key={`${x.vehicleId}-${x.key}-${x.dueOn}`} className="flex items-start justify-between gap-3 px-4 py-2.5 border-b border-slate-50 last:border-b-0">
                <div className="min-w-0">
                  <Link to={x.link} className="text-sm font-medium text-slate-900 hover:text-indigo-600">{x.title}</Link>
                  <p className="text-xs text-slate-500 truncate">
                    {vehicleId === undefined && <>{x.vehicleName} · </>}{x.basis}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-xs font-semibold tabular-nums ${x.left <= 7 ? 'text-red-600' : x.left <= 30 ? 'text-amber-700' : 'text-slate-600'}`}>{leftLabel(x.left)}</div>
                  <div className="text-xs text-slate-400 tabular-nums">{x.dueOn.replace(/-/g, '.')}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="bg-white border border-slate-200 rounded-lg">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">
          <Scale size={14} className="text-slate-400" /> 투자 의무
        </h2>
        {checks.length === 0 ? <p className="py-8 text-center text-sm text-slate-500">해당 의무 없음</p> : (
          <ul>
            {checks.map((c) => {
              const st = CHECK_STATUS[c.status];
              const bar = c.ratio === null ? 0 : Math.min(100, (c.ratio / Math.max(c.threshold, 0.0001)) * 100);
              return (
                <li key={`${c.vehicleId}-${c.key}`} className="px-4 py-2.5 border-b border-slate-50 last:border-b-0">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-900">{vehicleId === undefined && <span className="text-slate-500 font-normal">{c.vehicleName} · </span>}{c.title}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${st.style}`}>{st.label}</span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full ${c.status === 'ok' ? 'bg-emerald-500' : c.status === 'unknown' ? 'bg-slate-400' : 'bg-amber-500'}`} style={{ width: `${bar}%` }} />
                    </div>
                    <span className="text-xs tabular-nums text-slate-600 w-28 text-right">
                      {c.ratio === null ? '—' : `${(c.ratio * 100).toFixed(1)}%`} / {c.op === 'min' ? '≥' : '≤'} {c.threshold * 100}%
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500 truncate" title={[c.basis, c.note].filter(Boolean).join(' · ')}>
                    {c.op === 'min' ? '초기창업' : '상장사'} {won0(c.numerator)} / {c.vehicleId && c.key !== 'ac-early-stage' ? '출자금' : '전체 투자'} {won0(c.base)}
                    {c.deadline && <> · 기한 {c.deadline.replace(/-/g, '.')}</>}
                    {c.note && <span className="text-amber-700"> · {c.note}</span>}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};
